import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, ChevronLeft, ChevronRight, Instagram as InstagramIcon, Play, X } from "lucide-react";
import { INSTAGRAM } from "../data/content";
import { Reveal, SectionHeader } from "./ui-bits";

// Official Instagram embed player for public posts & reels — no token needed
function embedSrc(url) {
  const m = (url || "").match(/instagram\.com\/(reel|p)\/([A-Za-z0-9_-]+)/);
  return m ? `https://www.instagram.com/${m[1]}/${m[2]}/embed/` : null;
}

// curated list: detect POST/REEL from the link
const POSTS = INSTAGRAM.posts.map((p) => ({
  ...p,
  type: p.type || (p.url.includes("/reel/") ? "REEL" : "POST"),
}));

export default function Instagram() {
  const [posts, setPosts] = useState(POSTS);
  const [viewer, setViewer] = useState(null);
  const trackRef = useRef(null);
  const [page, setPage] = useState(0);
  const [pages, setPages] = useState(1);

  // Lock page scroll (Lenis + body) while the viewer is open
  useEffect(() => {
    if (!viewer) return;
    const lenis = window.__lenis;
    lenis?.stop?.();
    document.body.style.overflow = "hidden";
    const esc = (e) => e.key === "Escape" && setViewer(null);
    window.addEventListener("keydown", esc);
    return () => {
      lenis?.start?.();
      document.body.style.overflow = "";
      window.removeEventListener("keydown", esc);
    };
  }, [viewer]);

  // slider pager
  const updatePager = () => {
    const el = trackRef.current;
    if (!el) return;
    const total = Math.max(1, Math.ceil(el.scrollWidth / el.clientWidth));
    setPages(total);
    const max = el.scrollWidth - el.clientWidth;
    setPage(max > 0 ? Math.round((el.scrollLeft / max) * (total - 1)) : 0);
  };
  useEffect(() => {
    updatePager();
  }, [posts]);
  const slide = (dir) => {
    const el = trackRef.current;
    el?.scrollBy({ left: dir * el.clientWidth, behavior: "smooth" });
  };

  return (
    <section id="instagram" data-testid="instagram-section" className="relative scroll-mt-20 overflow-hidden py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <SectionHeader
          index="09"
          station="LIVE FROM THE TRAIL"
          id="FEED // TRAIL DISPATCHES"
          title={<>Postcards from<br /><span className="text-ember">the Trail</span></>}
          sub="Selected dispatches from the mountains — posts and reels from the expeditions, straight from Instagram. Slide through and tap a tile to screen it full-size, right here on the page."
        />

        <Reveal>
          <div className="mb-5 flex items-center justify-between gap-4" data-testid="instagram-nav">
            <p className="font-mono text-[9px] tracking-[0.3em] text-cream/50">TAP A DISPATCH TO SCREEN IT</p>
            <div className="flex items-center gap-3">
              <span className="font-mono text-[10px] tracking-[0.25em] text-cream/50" data-testid="instagram-pager">
                {String(page + 1).padStart(2, "0")} / {String(pages).padStart(2, "0")}
              </span>
              <button
                onClick={() => slide(-1)}
                disabled={page === 0}
                data-testid="instagram-prev"
                aria-label="Previous dispatches"
                className="flex h-9 w-9 items-center justify-center border border-cream/20 text-cream transition-colors hover:border-ember hover:text-ember disabled:opacity-30"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={() => slide(1)}
                disabled={page >= pages - 1}
                data-testid="instagram-next"
                aria-label="Next dispatches"
                className="flex h-9 w-9 items-center justify-center border border-cream/20 text-cream transition-colors hover:border-ember hover:text-ember disabled:opacity-30"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div
            ref={trackRef}
            onScroll={updatePager}
            data-testid="instagram-track"
            className="flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-smooth md:gap-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {posts.slice(0, 7).map((p, i) => (
              <div key={`${p.url}-${i}`} className="w-[calc(50%-6px)] shrink-0 snap-start sm:w-[calc(33.333%-8px)] lg:w-[calc(16.6667%-13.334px)]">
                <a
                  href={p.url || INSTAGRAM.profile}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => {
                    e.preventDefault();
                    setViewer(p);
                  }}
                  data-testid={`instagram-tile-${i + 1}`}
                  className="group relative block cursor-pointer overflow-hidden border border-white/10 transition-colors duration-300 hover:border-ember/70"
                >
                  {p.img ? (
                    <img
                      src={p.img}
                      alt={p.caption || "Instagram dispatch"}
                      loading="lazy"
                      className="aspect-[9/16] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  ) : (
                    <div className="flex aspect-[9/16] w-full flex-col items-center justify-center gap-3 bg-ink/70 p-4" data-testid={`instagram-tile-placeholder-${i + 1}`}>
                      <InstagramIcon className="h-9 w-9 text-cream/25 transition-colors duration-300 group-hover:text-ember/70" />
                      <p className="font-mono text-[9px] tracking-[0.3em] text-cream/40">DISPATCH 0{i + 1}</p>
                      <p className="flex items-center gap-1.5 font-mono text-[8px] font-bold tracking-[0.25em] text-ember">
                        TAP TO VIEW <Play className="h-2.5 w-2.5 fill-current" />
                      </p>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-night/90 via-transparent to-night/20" />
                  <span
                    data-testid={`instagram-tile-type-${i + 1}`}
                    className="absolute left-3 top-3 flex items-center gap-1.5 border border-white/25 bg-night/60 px-2 py-1 font-mono text-[8px] font-bold tracking-[0.25em] text-cream backdrop-blur-sm"
                  >
                    {p.type === "REEL" ? <Play className="h-2.5 w-2.5 fill-current" /> : <InstagramIcon className="h-2.5 w-2.5" />}
                    {p.type}
                  </span>
                  {p.type === "REEL" && (
                    <span className="absolute inset-0 flex items-center justify-center">
                      <span className="flex h-12 w-12 items-center justify-center rounded-full border border-white/40 bg-night/50 backdrop-blur-sm transition-all duration-300 group-hover:scale-110 group-hover:border-ember group-hover:bg-ember">
                        <Play className="h-5 w-5 fill-current text-cream transition-colors duration-300 group-hover:text-night" />
                      </span>
                    </span>
                  )}
                </a>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <div className="mt-10 flex flex-wrap items-center justify-between gap-4 border border-white/10 bg-ink/60 px-5 py-4" data-testid="instagram-footer">
            <span className="font-mono text-[9px] tracking-[0.25em] text-cream/50" data-testid="instagram-feed-status">
              {INSTAGRAM.handle.toUpperCase()} — SELECTED DISPATCHES
            </span>
            <a
              href={INSTAGRAM.profile}
              target="_blank"
              rel="noreferrer"
              data-testid="instagram-follow-cta"
              className="btn-fill flex items-center gap-2 px-4 py-2 font-mono text-[10px] font-bold tracking-[0.22em] text-white transition-colors hover:border-ember hover:text-ember"
            >
              FOLLOW THE EXPEDITION <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
          </div>
        </Reveal>
      </div>

      {/* full-screen trail viewer */}
      <AnimatePresence>
        {viewer && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[90] flex items-center justify-center bg-night/95 p-4 backdrop-blur-sm"
            onClick={() => setViewer(null)}
            data-testid="reel-viewer"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 12 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="flex max-h-[92vh] w-full max-w-[420px] flex-col overflow-hidden border border-cream/15 bg-ink"
              onClick={(e) => e.stopPropagation()}
              data-testid="reel-viewer-frame"
            >
              <div className="flex items-center justify-between border-b border-cream/10 px-4 py-3">
                <span className="flex items-center gap-2.5 font-mono text-[9px] font-bold tracking-[0.3em] text-cream/60">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-ember" />
                  {viewer.type === "REEL" ? "TRAIL REEL — NOW PLAYING" : "TRAIL POST — FULL FRAME"}
                </span>
                <button
                  onClick={() => setViewer(null)}
                  data-testid="reel-viewer-close"
                  className="p-1 text-cream/60 transition-colors hover:text-ember"
                  aria-label="Close viewer"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="flex min-h-0 flex-1 items-center justify-center bg-night/60">
                {viewer.video ? (
                  <video
                    src={viewer.video}
                    controls
                    autoPlay
                    playsInline
                    loop
                    className="max-h-[62vh] w-full bg-black object-contain"
                    data-testid="reel-viewer-video"
                  />
                ) : embedSrc(viewer.url) ? (
                  <iframe
                    src={embedSrc(viewer.url)}
                    title="Instagram reel player"
                    scrolling="no"
                    allowFullScreen
                    className="h-[62vh] w-full bg-black"
                    style={{ border: 0 }}
                    data-testid="reel-viewer-embed"
                  />
                ) : (
                  <img
                    src={viewer.img}
                    alt={viewer.caption || "Instagram dispatch"}
                    className="max-h-[62vh] w-full bg-black object-contain"
                    data-testid="reel-viewer-image"
                  />
                )}
              </div>

              <div className="flex items-center justify-between gap-4 border-t border-cream/10 px-4 py-3">
                <p className="font-mono text-[10px] tracking-[0.2em] text-cream/50">{INSTAGRAM.handle.toUpperCase()}</p>
                <a
                  href={viewer.url || INSTAGRAM.profile}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="reel-viewer-open-original"
                  className="flex shrink-0 items-center gap-1.5 border border-cream/20 px-3 py-2 font-mono text-[9px] font-bold tracking-[0.2em] text-cream transition-colors hover:border-ember hover:text-ember"
                >
                  INSTAGRAM <ArrowUpRight className="h-3 w-3" />
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
